import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Layout } from '../../components/layout/Layout';
import { PageHeader } from '../../components/layout/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Alert } from '../../components/ui/Alert';
// import { Input } from '../../components/ui/Input';
import { useAuth } from '../../contexts/AuthContext';
import { inviteTeacher } from '../../modules/platform/api/schools';
import { getCreatedInvites, getSchoolInvites } from '../../modules/platform/api/devteam';
import { showErrorToast, showSuccessToast } from '../../utils/errorHandler';
// import { validateEmail } from '../../utils/validation';
import { InviteModal } from './InviteModal';

interface InviteFormData {
  email: string;
}

export const InviteTeacher: React.FC = () => {
  const { user } = useAuth();
  const [_isLoading, setIsLoading] = useState(false); // eslint-disable-line @typescript-eslint/no-unused-vars
  const [invites, setInvites] = useState<any[]>([]);
  const [loadingInvites, setLoadingInvites] = useState(true);
  const [otherInvites, setOtherInvites] = useState<any[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const schoolId = user?.memberships[0]?.school_id;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<InviteFormData>();

  const onSubmit = async (data: InviteFormData) => {
    if (!schoolId) return;

    setIsLoading(true);
    try {
      await inviteTeacher(schoolId, data.email);
      showSuccessToast('Invite sent successfully!');
      reset();
      await loadInvites();
    } catch (error) {
      showErrorToast(error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadInvites = async () => {
    setLoadingInvites(true);
    try {
      const data = schoolId ? await getSchoolInvites(schoolId, 'teacher') : await getCreatedInvites();
      setInvites(data || []);

      // If there are no teacher invites, check for invites of other purposes for the same school
      if ((data || []).length === 0 && schoolId) {
        try {
          const all = await getSchoolInvites(schoolId);
          const others = (all || []).filter((inv: any) => inv.purpose !== 'teacher');
          setOtherInvites(others);
        } catch {
          setOtherInvites([]);
        }
      } else {
        setOtherInvites([]);
      }
    } catch (err) {
      showErrorToast(err);
    } finally {
      setLoadingInvites(false);
    }
  };

  useEffect(() => {
    loadInvites();
  }, []);

  return (
    <Layout>
      <PageHeader
        title="Invite Teacher"
        subtitle="Send an invitation to a teacher"
        actions={<Button onClick={() => setModalOpen(true)}>Invite</Button>}
      />

      <Card className="max-w-2xl">
        <CardHeader>
          <CardTitle>Recent Invites</CardTitle>
        </CardHeader>
        <CardContent>
          {loadingInvites ? (
            <div>Loading...</div>
          ) : invites.length === 0 ? (
            <>
              {otherInvites.length > 0 ? (
                <Alert variant="warning" className="mb-2">
                  No teacher invites found for this school. There are {otherInvites.length} invite(s) with other purposes (e.g. {Array.from(new Set(otherInvites.map(i => i.purpose))).join(', ')}).
                </Alert>
              ) : null}
              <div className="text-sm text-gray-500">No invites yet.</div>
            </>
          ) : (
            <table className="w-full table-auto">
              <thead>
                <tr className="text-left">
                  <th className="p-2">Email</th>
                  <th className="p-2">Purpose</th>
                  <th className="p-2">Status</th>
                  <th className="p-2">Sent</th>
                  <th className="p-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {invites.map((inv) => (
                  <tr key={inv._id} className="border-t">
                    <td className="p-2">{inv.email}</td>
                    <td className="p-2">{inv.purpose}</td>
                    <td className="p-2">{inv.status}</td>
                    <td className="p-2 text-sm text-gray-500">{inv.sent_at ? new Date(inv.sent_at).toLocaleString() : '-'}</td>
                    <td className="p-2">
                      <Button variant="ghost" size="sm" onClick={() => { /* future: show details */ }}>Details</Button>
                      <Button variant="ghost" size="sm" onClick={() => { /* future: resend */ }}>Resend</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <InviteModal isOpen={modalOpen} onClose={() => setModalOpen(false)} schoolId={schoolId} onInvited={loadInvites} />
    </Layout>
  );
};

